import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { TOTP, totpAuthenticatorQueryKeys } from '#api';
import type { Successful } from '#dn-types/api.ts';
import type { TOTPAuthenticatorListItem } from '#dn-types/totp-authenticators.ts';

export const totpAuthenticatorsQueryOptions = queryOptions({
  queryKey: totpAuthenticatorQueryKeys.list(),
  queryFn: ({ signal }) => TOTP.getAuthenticators({ signal }),
});

export function useSuspenseTOTPAuthenticatorsQuery() {
  return useSuspenseQuery(totpAuthenticatorsQueryOptions);
}

const makeTOTPAuthenticatorQueryOptions = (id: string) =>
  queryOptions({
    queryKey: totpAuthenticatorQueryKeys.detail(id),
    queryFn: ({ signal }) => TOTP.getAuthenticator(id, { signal }),
  });

/**
 * {@link useSuspenseTOTPAuthenticatorQuery} fetches authenticator details from the api, using cached results first if possible.
 */
export function useSuspenseTOTPAuthenticatorQuery(id: string) {
  const queryClient = useQueryClient();

  return useSuspenseQuery({
    ...makeTOTPAuthenticatorQueryOptions(id),
    initialData: () => {
      const totpQueryResults =
        queryClient.getQueriesData<Successful<TOTPAuthenticatorListItem[] | null>>(totpAuthenticatorsQueryOptions);
      const authenticator = totpQueryResults
        .flatMap((hit) => hit[1]?.data)
        .find((d) => {
          return d?.id === id;
        });

      // If we found a match, use it as the initial data for the query
      if (authenticator) {
        return { data: authenticator };
      }
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(totpAuthenticatorsQueryOptions.queryKey)?.dataUpdatedAt,
  });
}

export function useEditTOTPAuthenticator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: TOTP.editAuthenticator,
    onSuccess: (data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: totpAuthenticatorQueryKeys.lists() });
      queryClient.setQueryData(makeTOTPAuthenticatorQueryOptions(id).queryKey, { data });
    },
  });
}
